import React, { useState, useEffect, useRef } from "react";
import styles from "../EditDiv/EditDiv.module.css";
import buttonStyles from "../YesNoButtonsCSS/YesNoButtons.module.css";
import BlueLogo from "../../assets/BlueLogo.png";
import { Grid, TextField, CircularProgress } from "@mui/material";
import { Form, FormikProvider } from "formik";
import { useFormik } from "formik";
import * as Yup from "yup";
import textfieldStyle from "../../GlobalStyles/styles";
import Dropdown from "../DropDownBox/Dropdown";
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from "../../redux/store";
import { submitItTicket, setSuccess, hasError } from "../../redux/slices/itTickets";
import { useMsal } from "@azure/msal-react";

const ITForm = ({ onSubmit, onClose, defaultEmail }) => {
  const initialValues = {
    requestType: "",
    urgency: "3",
    description: "",
    attachments: null,
    otherDetails: "",
    requesterEmail: "",
  };

  const dispatch = useDispatch();
  const itTicketsState = useSelector((state) => state.itTickets || {});
  const [apiResponse, setApiResponse] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { accounts } = useMsal();
  const account = accounts && accounts.length > 0 ? accounts[0] : null;
  const backdropRef = useRef(null);
  const formRef = useRef(null);

  // clear any previous success state when this form mounts so it won't auto-close
  React.useEffect(() => {
    dispatch(setSuccess(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect redux state into local UI
  React.useEffect(() => {
    if (itTicketsState.successMessage) {
      const msg = itTicketsState.successMessage;
      enqueueSnackbar(msg, { variant: 'success' });
      // clear redux success so subsequent opens don't immediately close
      dispatch(setSuccess(null));
      // close form on success
      if (typeof onClose === 'function') onClose();
    }
    if (itTicketsState.error) {
      const errMsg = itTicketsState.error?.message || String(itTicketsState.error);
      enqueueSnackbar(errMsg, { variant: 'error' });
      // keep error in redux for debugging but clear it so UI won't be sticky
      dispatch(hasError(null));
    }
  }, [itTicketsState.successMessage, itTicketsState.error, enqueueSnackbar, onClose]);

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      requestType: Yup.string().required("Required"),
      urgency: Yup.string().required("Required"),
      description: Yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("requestType", values.requestType || "");
      formData.append("urgency", values.urgency || "");
      formData.append("description", values.description || "");
      formData.append("otherDetails", values.otherDetails || "");
      if (values.requesterEmail) formData.append("requesterEmail", values.requesterEmail);
      if (values.attachments && values.attachments.length) {
        Array.from(values.attachments).forEach((file, idx) => {
          formData.append(`file_${idx}`, file, file.name);
        });
      }

      try {
        await dispatch(submitItTicket(formData));
        // success handled by selector effect
      } catch (e) {
        // error handled by selector effect
      }
    },
  });

  // Set requesterEmail from MSAL username or provided defaultEmail
  useEffect(() => {
    const autoEmail = (typeof defaultEmail === 'string' && defaultEmail) || account?.username || '';
    if (autoEmail) formik.setFieldValue('requesterEmail', autoEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail, account]);

  // Ensure modal opens scrolled to the top and content is top-aligned
  useEffect(() => {
    if (backdropRef.current) backdropRef.current.scrollTop = 0;
    if (formRef.current) formRef.current.scrollTop = 0;
  }, []);

  const requestTypeOptions = [
    "Flow Issue",
    "Moment Issue",
    "Software Issue",
    "Hardware Issue",
    "SharePoint or OneDrive Issue",
    "Template Update",
    "Suggestion",
    "Other",
  ];

  const handleFileChange = (e) => {
    formik.setFieldValue("attachments", e.target.files);
  };

  return (
    <div className={styles.backGround} ref={backdropRef}>
      <div
        className={styles.form}
        ref={formRef}
        style={{
          width: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          justifyContent: "flex-start",
          alignItems: "stretch",
          paddingTop: 12,
          paddingBottom: 8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>IT Request</div>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate style={{ width: "100%" }} onSubmit={formik.handleSubmit}>
            <Grid container sx={{ width: "100%" }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>1. What is the nature of your IT request? *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <Dropdown
                  options={requestTypeOptions}
                  placeholder={"Select..."}
                  initialSelectedValue={formik.values.requestType}
                  onValueChange={(val) => formik.setFieldValue("requestType", val)}
                />
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>2. Urgency *</p>
                <div style={{ fontSize: 12, color: "#666" }}>
                  5 means you need it done in the next couple of working hours.
                </div>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div
                      key={n}
                      onClick={() => formik.setFieldValue("urgency", String(n))}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 4,
                        border: formik.values.urgency === String(n) ? "2px solid #3C96D4" : "1px solid #ddd",
                        cursor: "pointer",
                        background: formik.values.urgency === String(n) ? "#e9f5fb" : "#fff",
                      }}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>3. Please provide a description of your query *</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={(e) => formik.setFieldValue("description", e.target.value)}
                  placeholder="Enter your answer"
                  sx={textfieldStyle}
                  error={Boolean(formik.touched.description && formik.errors.description)}
                />
              </Grid>
            </Grid>

            <Grid container sx={{ width: "100%", mt: 2 }}>
              <Grid item xs={4} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>4. Upload Any Relevant documents (e.g screenshots)</p>
              </Grid>
              <Grid item xs={8} sx={{ mt: 1 }}>
                <input type="file" multiple onChange={handleFileChange} />
              </Grid>
            </Grid>

            <div className={styles.updateAndCancel} style={{ marginTop: 16 }}>
              <div className={styles.updateButton} type="submit" onClick={formik.handleSubmit}>
                <div className={buttonStyles.YesButtonComponentStyling}>
                  {itTicketsState.isSubmitting ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'SUBMIT'}
                </div>
              </div>
              <div className={styles.cancelButton} onClick={onClose}>
                <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
              </div>
            </div>
          </Form>
        </FormikProvider>

        <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" style={{ alignSelf: 'center' }} />
      </div>
    </div>
  );
};

export default ITForm;


